// src/components/auth/NoCatalogAccessPage.tsx
//
// Full-page denial shown when an authenticated user lacks catalog:read.
// Rendered as the fallback of the outer <ProtectedRoute> in App.tsx — we can't
// redirect to "/" here because "/" is exactly the route we just got denied
// from, so we'd loop and render nothing.
//
// Accessibility: heading hierarchy + role="alert" so screen readers announce
// the denial. Actions provide a path forward (sign out, mailto admin).

import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { LockOutlined as LockIcon, MailOutline as MailIcon } from '@mui/icons-material';
import { useAuth } from '@ciea.ai/smart-auth-lib';

const SUPPORT_EMAIL =
  (import.meta.env.VITE_SUPPORT_EMAIL as string | undefined) || 'support@uvision.ai';

export const NoCatalogAccessPage = () => {
  const { logout, getCurrentUserEmail } = useAuth();
  const email = getCurrentUserEmail?.();

  const mailtoHref =
    `mailto:${SUPPORT_EMAIL}` +
    `?subject=${encodeURIComponent('Smart Catalog access request')}` +
    `&body=${encodeURIComponent(
      `Hi,\n\nMy account (${email ?? 'unknown email'}) doesn't have access to the Smart Catalog app. ` +
        `Please assign me an appropriate role (Catalog Viewer / Editor / Reviewer / Admin).\n\nThanks.`,
    )}`;

  return (
    <Box
      role="main"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        bgcolor: '#f1f5f9',
        px: 2,
      }}
    >
      <Paper
        elevation={2}
        role="alert"
        aria-live="polite"
        sx={{
          maxWidth: 520,
          width: '100%',
          p: { xs: 4, sm: 5 },
          borderRadius: 3,
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 72,
            height: 72,
            borderRadius: '50%',
            bgcolor: 'rgba(14, 165, 233, 0.12)',
            color: '#0EA5E9',
            mb: 3,
          }}
        >
          <LockIcon sx={{ fontSize: 36 }} />
        </Box>

        <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1F3A5F', mb: 1 }}>
          No Catalog access
        </Typography>

        {email && (
          <Typography variant="body2" sx={{ color: '#64748B', mb: 2 }}>
            Signed in as <strong>{email}</strong>
          </Typography>
        )}

        <Typography variant="body1" sx={{ color: '#475569', mb: 4, lineHeight: 1.6 }}>
          Your account is authenticated but does not have permission to use the Smart Catalog app.
          Ask an administrator to assign you a catalog role (Catalog Viewer, Editor, Reviewer, or
          Admin) in the Organization Management app, then sign out and back in to refresh your
          permissions.
        </Typography>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center">
          <Button
            variant="contained"
            startIcon={<MailIcon />}
            href={mailtoHref}
            sx={{ minWidth: 180 }}
          >
            Request access
          </Button>
          <Button
            variant="outlined"
            onClick={() => logout()}
            sx={{ minWidth: 180 }}
          >
            Sign out
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};
